import { useState } from 'react'
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Layers } from "lucide-react"
import L from 'leaflet'
import type { TileLayer } from './tile-layer-renderer'

const TILE_SERVER_HOST = "https://tile-server.asci.co.in"

interface TileLayerControlsProps {
  layers: TileLayer[]
  onLayerAdd: (layer: TileLayer) => void
  onLayerToggle: (layerId: string) => void
  onLayerRemove: (layerId: string) => void
  onLayerOpacityChange: (layerId: string, opacity: number) => void
}

/**
 * Component for adding and managing tile layers
 */
export function TileLayerControls({
  layers,
  onLayerAdd,
  onLayerToggle,
  onLayerRemove,
  onLayerOpacityChange
}: TileLayerControlsProps) {
  const [newLayerUrl, setNewLayerUrl] = useState('')
  const [newLayerColormap, setNewLayerColormap] = useState('rdylgn')
  const [newLayerRescale, setNewLayerRescale] = useState('550,600')
  const [isLoadingLayer, setIsLoadingLayer] = useState(false)

  const addTileLayer = async () => {
    if (!newLayerUrl.trim()) return

    setIsLoadingLayer(true)
    const geoTiffUrl = newLayerUrl.trim()
    const colormap = newLayerColormap.trim() || 'rdylgn'
    const rescale = newLayerRescale.trim() || '550,600'

    try {
      // Get bounds from tile server
      const boundsResponse = await fetch(
        `${TILE_SERVER_HOST}/bounds?url=${encodeURIComponent(geoTiffUrl)}`
      )

      if (!boundsResponse.ok) {
        throw new Error(`Failed to get bounds: ${boundsResponse.status}`)
      }

      const boundsData = await boundsResponse.json()
      const [minX, minY, maxX, maxY] = boundsData.bounds
      const bounds = L.latLngBounds(
        L.latLng(minY, minX),
        L.latLng(maxY, maxX)
      )

      const titilerTileUrl = `${TILE_SERVER_HOST}/tiles/WebMercatorQuad/{z}/{x}/{y}.png?url=${encodeURIComponent(
        geoTiffUrl
      )}&colormap_name=${colormap}&rescale=${encodeURIComponent(rescale)}`

      const layerId = `layer_${Date.now()}`
      const layerName = geoTiffUrl.split('/').pop() || `Layer ${layers.length + 1}`

      const newLayer: TileLayer = {
        id: layerId,
        name: layerName,
        url: titilerTileUrl,
        geoTiffUrl: geoTiffUrl,
        visible: true,
        opacity: 0.7,
        colormap: colormap,
        rescale: rescale,
        bounds: bounds
      }

      onLayerAdd(newLayer)
      setNewLayerUrl('')
      setNewLayerColormap('rdylgn')
      setNewLayerRescale('550,600')

    } catch (error) {
      console.error('Error loading TIF via tile server:', error)
      alert(`Error loading TIF: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setIsLoadingLayer(false)
    }
  }

  return (
    <Card className="p-3 bg-background/90 backdrop-blur-sm shadow-lg">
      <h4 className="text-sm font-semibold mb-3">Tile Layers</h4>

      {/* Add new layer input */}
      <div className="space-y-2 mb-3">
        <input
          type="text"
          placeholder="Enter GeoTIFF URL..."
          value={newLayerUrl}
          onChange={(e) => setNewLayerUrl(e.target.value)}
          className="w-full px-2 py-1 text-xs border rounded"
        />

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Colormap</label>
            <select
              value={newLayerColormap}
              onChange={(e) => setNewLayerColormap(e.target.value)}
              className="w-full px-2 py-1 text-xs border rounded"
            >
              <option value="rdylgn">RdYlGn</option>
              <option value="viridis">Viridis</option>
              <option value="plasma">Plasma</option>
              <option value="inferno">Inferno</option>
              <option value="magma">Magma</option>
              <option value="cividis">Cividis</option>
              <option value="turbo">Turbo</option>
              <option value="rainbow">Rainbow</option>
              <option value="jet">Jet</option>
            </select>
          </div>

          <div>
            <label className="block text-xs text-muted-foreground mb-1">Rescale</label>
            <input
              type="text"
              placeholder="min,max"
              value={newLayerRescale}
              onChange={(e) => setNewLayerRescale(e.target.value)}
              className="w-full px-2 py-1 text-xs border rounded"
            />
          </div>
        </div>

        <Button
          size="sm"
          variant="outline"
          onClick={addTileLayer}
          disabled={isLoadingLayer || !newLayerUrl.trim()}
          className="w-full"
        >
          {isLoadingLayer ? (
            <>
              <div className="animate-spin h-3 w-3 border border-current border-t-transparent rounded-full mr-2"></div>
              Loading...
            </>
          ) : (
            <>
              <Layers className="h-4 w-4 mr-2" />
              Add Layer
            </>
          )}
        </Button>
      </div>

      {/* Layer list */}
      {layers.length > 0 && (
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {layers.map((layer) => (
            <div key={layer.id} className="border rounded p-2">
              <div className="flex items-center justify-between mb-2">
                <div className="flex-1">
                  <span className="text-xs font-medium truncate block" title={layer.name}>
                    {layer.name}
                  </span>
                  {layer.bounds && (
                    <span className="text-xs text-muted-foreground block">
                      📍 {layer.bounds.getSouth().toFixed(1)},{layer.bounds.getWest().toFixed(1)}
                    </span>
                  )}
                </div>
                <div className="flex gap-1">
                  <input
                    type="checkbox"
                    checked={layer.visible}
                    onChange={() => onLayerToggle(layer.id)}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onLayerRemove(layer.id)}
                    className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                  >
                    ×
                  </Button>
                </div>
              </div>
              {layer.visible && (
                <div>
                  <label className="text-xs text-muted-foreground">
                    Opacity: {Math.round(layer.opacity * 100)}%
                  </label>
                  <input
                    type="range"
                    min="0.1"
                    max="1"
                    step="0.1"
                    value={layer.opacity}
                    onChange={(e) => onLayerOpacityChange(layer.id, parseFloat(e.target.value))}
                    className="w-full h-1"
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}
